/**
 * @fileoverview GlobalAnka — Uygulama Başlangıç Noktası (Entry Point)
 *
 * Bu dosya projenin tek ve mutlak başlangıç noktasıdır.
 * Aşağıdaki sıra kritik olup değiştirilmemelidir:
 *
 *  1. Sentry Init      — Tüm React ağacından önce başlatılır (hata yakalamak için).
 *  2. i18n Init        — Uygulama mount edilmeden önce dil kaynakları hazır olmalı.
 *  3. Stil İmportları  — CSS/Tailwind, herhangi bir bileşenden önce yüklenir.
 *  4. React Render     — Provider zinciri aşağıdaki hiyerarşiyi takip etmek ZORUNDADIR:
 *
 *     StrictMode
 *       └─ ErrorBoundary          (tüm ağacı saran son savunma hattı)
 *            └─ Redux Provider    (global state: language, ui, portfolio, leadForm)
 *                 └─ PersistGate  (localStorage rehydration tamamlanana kadar bloke)
 *                      └─ HelmetProvider  (per-page SEO meta tag yönetimi)
 *                           └─ AppRouter  (route tanımları + layout + sayfalar)
 *
 * @module main
 * @author GlobalAnka Dev Team
 * @version 1.0.0 — V1 MVP
 */

import React from 'react'
import ReactDOM from 'react-dom/client'

// ─────────────────────────────────────────────────────────────────────────────
// 1. SENTRY INIT
//    initSentry(), React render ağacı kurulmadan önce çağrılmalıdır.
//    DEV ortamında: no-op (konsola bilgi mesajı yazar, veri göndermez)
//    PROD ortamında: browserTracing + replay entegrasyonu ile aktif olur.
// ─────────────────────────────────────────────────────────────────────────────
import { initSentry } from './config/sentry'

initSentry()

// ─────────────────────────────────────────────────────────────────────────────
// 2. i18N INIT
//    TR senkron; EN/RU ayrı chunk — kalıcı dil + initI18nBootstrap (await).
// ─────────────────────────────────────────────────────────────────────────────
import { initI18nBootstrap } from './i18n/index.js'

// ─────────────────────────────────────────────────────────────────────────────
// 3. STİL İMPORTLARI
//    Tailwind v4 @theme token'ları + utility class'lar burada yüklenir.
//    App.css veya src/index.css kullanılmaz; tüm stiller styles/index.css'ten gelir.
// ─────────────────────────────────────────────────────────────────────────────
import './styles/index.css'

await initI18nBootstrap()

// ─────────────────────────────────────────────────────────────────────────────
// 4. PROVIDER VE ROUTER İMPORTLARI
// ─────────────────────────────────────────────────────────────────────────────
import { Provider }          from 'react-redux'
import { PersistGate }       from 'redux-persist/integration/react'
import { HelmetProvider }    from 'react-helmet-async'
import { store, persistor }  from './app/store'
import AppRouter             from './routes/AppRouter'
import ErrorBoundary         from './errors/ErrorBoundary'

// ─────────────────────────────────────────────────────────────────────────────
// 5. REACT RENDER
// ─────────────────────────────────────────────────────────────────────────────

/** Root DOM düğümü — index.html'deki <div id="root"> */
const rootElement = document.getElementById('root')

if (!rootElement) {
  throw new Error('[GlobalAnka] Root element bulunamadı. index.html yapısı bozuk olabilir.')
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    {/* Global ErrorBoundary — tüm render ağacının en dış güvenlik katmanı */}
    <ErrorBoundary
      fallback={
        <div
          role="alert"
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#0D172A',
            color: 'rgba(255,255,255,0.7)',
            fontFamily: '"DM Sans", system-ui, sans-serif',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <p
            style={{
              fontSize: '3rem',
              fontFamily: '"Playfair Display", serif',
              background: 'linear-gradient(90deg,#BF953F,#FCF6BA,#AA771C)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            GlobalAnka
          </p>
          <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.5)' }}>
            Kritik bir hata oluştu. Lütfen sayfayı yenileyin.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.75rem 2rem',
              background: 'linear-gradient(135deg,#BF953F,#FCF6BA,#AA771C)',
              color: '#1A1A1A',
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Yenile
          </button>
        </div>
      }
    >
      {/*
       * Redux Provider — Tüm slice state'lerini (ui, language, portfolio, leadForm)
       * ve dispatch fonksiyonunu React ağacına enjekte eder.
       */}
      <Provider store={store}>
        {/*
         * PersistGate — redux-persist localStorage'dan state'i rehydrate edene
         * kadar uygulamanın render edilmesini engeller.
         * loading prop: rehydration süresi boyunca gösterilecek UI (FullScreen Spinner).
         */}
        <PersistGate loading={null} persistor={persistor}>
          {/*
           * HelmetProvider — react-helmet-async'ın SSR-safe per-page <head>
           * yöneticisi. Her sayfadan <Helmet> ile title/meta override edilebilir.
           */}
          <HelmetProvider>
            {/*
             * AppRouter — BrowserRouter + Route tanımları + lazy page loading.
             * Tüm sayfalar, layout'lar ve hata sınırları bu bileşen içinde tanımlıdır.
             */}
            <AppRouter />
          </HelmetProvider>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  </React.StrictMode>
)
